/**
 * BACK-19 — foco de overlays: vai ao `initialFocusRef` (ou ao 1º focável do painel), fica preso
 * no painel com Tab/Shift+Tab e volta ao gatilho ao fechar. Extraído de `ConfirmDialog` e
 * `useDialogA11y` (mesmo contrato).
 */
import { useEffect, useRef, type RefObject } from 'react'

export const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export interface FocusTrapOptions {
  panelRef?: RefObject<HTMLElement | null>
  initialFocusRef?: RefObject<HTMLElement | null>
  /** Prende o Tab no painel (padrão true; requer `panelRef`). */
  trap?: boolean
  /** Devolve o foco ao elemento ativo antes de abrir (padrão true). */
  restoreFocus?: boolean
  /** Atraso do foco inicial (padrão 0 ms, para esperar o painel montar). */
  initialFocusDelayMs?: number
}

const focusablesIn = (panel: HTMLElement): HTMLElement[] =>
  Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.offsetParent !== null || element === document.activeElement,
  )

export function useFocusTrap(open: boolean, options: FocusTrapOptions = {}) {
  const { panelRef, initialFocusRef, trap = true, restoreFocus = true, initialFocusDelayMs = 0 } = options
  const restoreFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) {
      if (restoreFocus) restoreFocusRef.current?.focus?.()
      restoreFocusRef.current = null
      return
    }

    restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const id = window.setTimeout(() => {
      const panel = panelRef?.current
      const target =
        initialFocusRef?.current ?? (panel ? (Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))[0] ?? null) : null)
      target?.focus()
    }, initialFocusDelayMs)
    return () => window.clearTimeout(id)
  }, [initialFocusDelayMs, initialFocusRef, open, panelRef, restoreFocus])

  useEffect(() => {
    if (!open || !trap || !panelRef) return

    // Listener no documento (não no painel): o painel pode montar depois do `open` (animação).
    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const panel = panelRef.current
      if (!panel || !(event.target instanceof Node) || !panel.contains(event.target)) return
      const focusables = focusablesIn(panel)
      if (focusables.length === 0) {
        // Ex.: assinatura em andamento: o título recebe o foco e nenhum controle pode sair dela.
        event.preventDefault()
        return
      }

      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement
      const activeIsFocusable = focusables.includes(active as HTMLElement)

      if (event.shiftKey) {
        if (active === first || !activeIsFocusable) {
          event.preventDefault()
          last.focus()
        }
        return
      }
      if (active === last || !activeIsFocusable) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleTabKey)
    return () => document.removeEventListener('keydown', handleTabKey)
  }, [open, panelRef, trap])
}
